<template>
  <SfModal v-if="modalOpen" :size="size" @close="close">
    <template #title>{{ editTitle }}</template>

    <template v-if="isRecurringChosenForVisibility" #messages>
      <SfModalMessage variant="info">
        <div v-dompurify-html="recurringTodoTopBannerLabel"></div>
      </SfModalMessage>
    </template>
    <template #body>
      <form
        class="sf-form sf-form-full sf-below-0 sf-c-text"
        data-walkthrough-id="edit-remark-modal"
      >
        <RemarkTypeSelector v-if="shouldShowTypeSelector" v-model="type" />

        <div class="sf-grid sf-grid-fluid">
          <div class="sf-row">
            <div class="sf-col">
              <div v-if="showPeriodSelector" class="sf-level sf-v-pad-1">
                <div class="sf-level-item">
                  <div class="sf-font-s sf-title">
                    {{ visibilityLabel }}
                  </div>
                </div>
                <div class="sf-level-item">
                  <PeriodVisibilityDropdown
                    :recurring-todo-template-id="recurringTodoTemplateId"
                    :is-edit-mode="true"
                    :visibility="visibility"
                    :ledger-label="ledgerLabel"
                    :is-todo="isTodo"
                    :square-bottom="showMentionHint"
                    @change-visibility="changeVisibility"
                  ></PeriodVisibilityDropdown>
                </div>
                <div class="sf-level-item">
                  <div class="sf-font-xs sf-dim-el">
                    {{ visibilityExplainLabel }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="sf-row">
            <div class="sf-col">
              <MarkdownEditorSection
                v-model="text"
                :errors="commsErrors.text"
                :mention-usernames="mentionUsernames"
                :mention-accounts="mentionAccounts"
                :mention-recon-templates="mentionReconTemplates"
                :is-loading-accounts="isLoadingAccounts"
                :is-loading-recon-templates="isLoadingReconTemplates"
                :square-bottom="showMentionHint"
                :company-id="companyId"
                :pulse-usernames="pulseUsernames"
                :period-private="periodPrivate"
                :period-name="privacyPeriodName"
                @hint-visibility-changed="hintVisibilityChanged"
              />
            </div>
          </div>
          <div class="sf-row">
            <div class="sf-col">
              <div class="sf-level sf-level-between">
                <div class="sf-level-item">
                  <div class="sf-level sf-block">
                    <span
                      v-for="attachment in allAttachments"
                      :key="attachment.id"
                    >
                      <div
                        class="
                          sf-label sf-label-subtle sf-below-1 sf-level-item
                        "
                      >
                        <div class="sf-level sf-level-small">
                          <div class="sf-level-item">
                            {{ attachment.filename }}
                          </div>
                          <div
                            class="sf-level-item fa fa-times sf-pointer"
                            @click.prevent="handleDeleteAttachment(attachment)"
                          ></div>
                        </div>
                      </div>
                    </span>
                  </div>
                </div>
                <DirectUploadDropzone
                  :multiple="true"
                  :on-file-progress="updateUploadProgress"
                  :on-file-uploaded="addLocalAttachment"
                  ><span
                    class="sf-pointer sf-a"
                    data-walkthrough-id="edit-remark-modal-add-attachment"
                    >{{
                      I18n.t('communication.remark_form.add_attachments')
                    }}</span
                  ></DirectUploadDropzone
                >
              </div>
            </div>
          </div>
          <div v-if="anyUnarchivedTags && showTags" class="sf-row">
            <div class="sf-col">
              <div class="sf-level sf-level-between">
                <RemarkTags
                  v-model="tagIds"
                  :all-tags="allRemarkTags"
                  :is-client-meeting-remark="isClientMeetingRemark"
                ></RemarkTags>
              </div>
            </div>
          </div>
          <div class="sf-row">
            <div class="sf-col">
              <div class="sf-level sf-level-between">
                <div class="sf-level">
                  <div class="sf-level-item">
                    <div
                      v-if="uploadInProgress"
                      class="sf-progress sf-progress-tiny sf-bg-snow"
                      style="width: 60px"
                    >
                      <div
                        :style="{ width: uploadProgress + '%' }"
                        class="sf-progress-complete sf-bg-primary"
                      ></div>
                    </div>
                  </div>
                </div>
                <a
                  :class="{ 'sf-btn-disabled': uploadInProgress }"
                  class="sf-btn sf-btn-success"
                  data-walkthrough-id="edit-remark-modal-save"
                  href="#"
                  @click.prevent="save"
                  >{{ I18n.t('communication.remark_form.save') }}</a
                >
              </div>
            </div>
          </div>
          <div v-if="hasErrors('base')">
            <SfInputError v-for="e in commsErrors.base" :key="e">
              {{ e }}
            </SfInputError>
          </div>
        </div>
      </form>
    </template>
  </SfModal>
</template>

<script>
import _ from 'lodash'

import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'
import RemarkTags from './remark-tags'
import { remarkDraftMixin, remarkTagsMixin, commonMethodsMixin } from './mixins'
import { SfInputError } from '../sf-input/index.ts'
import { SfModal, SfModalMessage } from '../sf-modal'
import api from '@app/application/communication/sections/communication/api/remark'
import { REMARK_ACTIONS } from '@app/application/communication/constants'
import attachmentsHelper from '@app/application/communication/sections/communication/components/mixins/attachments-helper'
import DirectUploadDropzone from '@app/shared/vue/components/direct-upload/direct-upload-dropzone'
import truncate from '@app/utils/truncate'
import useCommsError from '@app/application/communication/sections/communication/composables/use-comms-form-errors'

import PeriodVisibilityDropdown from './period-visibility-dropdown.vue'
import { VisibilityLevels } from './constants'
import RemarkTypeSelector from './remark-type-selector.vue'
import MarkdownEditorSection from './markdown-editor-section.vue'
import { useMentionablesStore } from '@app/application/communication/store/mentionables'

export default {
  components: {
    SfModal,
    SfInputError,
    DirectUploadDropzone,
    RemarkTags,
    SfModalMessage,
    PeriodVisibilityDropdown,
    RemarkTypeSelector,
    MarkdownEditorSection,
  },
  mixins: [
    attachmentsHelper,
    remarkDraftMixin,
    remarkTagsMixin,
    commonMethodsMixin,
  ],
  setup() {
    const { errors, hasErrors, handleErrors, clearTextErrors } = useCommsError()

    return {
      commsErrors: errors.errors,
      hasErrors,
      handleErrors,
      clearTextErrors,
      recurringTodoTopBannerLabel: I18n.t(
        'communication.remark_form.edit_recurring_todo_top_banner'
      ),
      visibilityLabel: I18n.t('communication.remark_form.sticky_label'),
    }
  },

  data() {
    return {
      id: null,
      ownerId: null,
      companyId: null,
      ownerType: null,
      ledgerId: null,
      ledgerLabel: null,
      endLedgerId: null,
      endLedgerLabel: null,
      visibility: null,
      type: null,
      text: null,
      attachmentIds: null,
      mentionUsernames: [],
      pulseUsernames: [],
      periodPrivate: false,
      privacyPeriodName: '',
      modalOpen: false,
      attachments: [],
      localAttachments: [],
      uploadProgress: 0,
      tagIds: [],
      hideTypeSelector: false,
      hidePeriodSelector: false,
      editTitle: null,
      showMentionHint: false,
      size: 's',
    }
  },

  computed: {
    ...mapPiniaState(useMentionablesStore, [
      'mentionAccounts',
      'mentionReconTemplates',
      'isLoadingAccounts',
      'isLoadingReconTemplates',
    ]),
    allAttachments() {
      return this.attachments
        .concat(this.localAttachments)
        .map((attachment) => ({
          ...attachment,
          filename: truncate(attachment.filename),
        }))
    },
    attachmentParams() {
      const params = []

      _.forEach(this.allAttachments, (attachment) => {
        if (attachment.isTemp) {
          params.push({
            documentDirectUploadPath: attachment.uploadPath,
          })
        } else {
          params.push({ id: attachment.id })
        }
      })

      return params
    },
    shouldShowTypeSelector() {
      return !this.hideTypeSelector && !this.isRecurringChosenForVisibility
    },
    isClientMeetingRemark() {
      return this.ownerType === 'ClientMeeting'
    },
  },

  watch: {
    text() {
      this.clearTextErrors()
    },
  },

  methods: {
    ...mapPiniaActions(useMentionablesStore, ['initialize']),
    async openModal(params) {
      const { originalRemark } = params

      this.id = originalRemark.id
      this.sticky = originalRemark.sticky
      this.companyId = originalRemark.companyId
      this.ownerId = originalRemark.contextId
      this.ownerType = originalRemark.contextType
      this.ledgerId = originalRemark.ledgerId
      this.ledgerLabel = originalRemark.ledgerPeriodName
      this.periodPrivate = !!originalRemark.periodPrivate
      // Warns against the viewed period, which may differ from the pin's starting period.
      this.privacyPeriodName =
        originalRemark.privacyPeriodName || originalRemark.ledgerPeriodName
      this.endLedgerId = originalRemark.endLedgerId
      this.endLedgerLabel = originalRemark.endLedgerPeriodName
      this.recurringTodoTemplateId = originalRemark.recurringTodoTemplateId
      this.type = originalRemark.todo ? 'todo' : 'note'
      this.attachments = originalRemark.attachments
      this.localAttachments = []
      this.uploadProgress = 0
      this.tagIds = originalRemark.tagIds || []
      this.allRemarkTags = originalRemark.allRemarkTags
      // when editing a Remark, the draft takes precedence, otherwise it would never be set
      this.text = this.loadDraft() || originalRemark.text
      this.showTags = originalRemark.showTags
      this.hideTypeSelector = originalRemark.hideTypeSelector
      this.hidePeriodSelector = originalRemark.hidePeriodSelector
      this.editTitle =
        originalRemark.editTitle || I18n.t('communication.edit_remark')
      this.size = params.modalSize || 's'
      await this.setMentionableUsernames()
      this.initialize(originalRemark.companyId, originalRemark.ledgerId)
      this.setDefaultVisibility()
      this.modalOpen = true
    },
    setDefaultVisibility() {
      if (this.recurringTodoTemplateId) {
        this.changeVisibility(VisibilityLevels.recurring)
      } else if (this.sticky) {
        this.changeVisibility(VisibilityLevels.pinned)
      } else {
        this.changeVisibility(VisibilityLevels.currentPeriod)
      }
    },
    async save() {
      if (this.isRecurringChosenForVisibility) {
        try {
          await this.validateRecurringTodo()
          this.askForPeriodScopeThenSave()
        } catch (error) {
          this.handleErrors(error)
        }
      } else {
        this.performSave()
      }
    },

    askForPeriodScopeThenSave() {
      window.modalCitizen.openModal('period-scope-for-update-modal', {
        onConfirm: (changeThisPeriodOnly) => {
          this.performSave(!changeThisPeriodOnly)
        },
      })
    },

    async validateRecurringTodo() {
      const params = {
        text: this.text,
        fileAttrs: this.attachmentParams,
        tagIds: this.tagIds,
      }

      return await api.validateRecurringTodo(this.id, params, this.companyId)
    },

    performSave(updateRecurringTodos = false) {
      const params = {
        ownerType: this.ownerType,
        ownerId: this.ownerId,
        text: this.text,
        ledgerId: this.ledgerId,
        todo: this.isTodo,
        sticky: this.isPinnedChosenForVisibility,
        fileAttrs: this.attachmentParams,
        tagIds: this.tagIds,
        update_recurring_todos: updateRecurringTodos,
      }

      api
        .update(this.id, params, this.companyId)
        .then(({ remark }) => {
          this.close()
          this.cleanupDraft()
          window.eventHub.$emit('refreshCommunicationData', {
            remark,
            action: REMARK_ACTIONS.EDITED,
          })
          window.eventHub.$emit('scrollToElement', {
            elementType: 'remark',
            elementId: this.id,
          })
        })
        .catch(this.handleErrors)
    },

    addLocalAttachment({ blob }) {
      this.localAttachments.push({
        id: blob.id,
        filename: blob.filename,
        type: blob.content_type,
        size: blob.byte_size,
        uploadPath: blob.signed_id,
        isTemp: true,
      })
    },

    handleDeleteAttachment(attachment) {
      if (attachment.isTemp) {
        const index = this.localAttachments.indexOf(attachment)
        this.localAttachments.splice(index, 1)
      } else {
        const index = this.attachments.indexOf(attachment)
        this.attachments.splice(index, 1)
      }
    },
    setNote() {
      this.type = 'note'
    },
    hintVisibilityChanged(value) {
      this.showMentionHint = value
    },
  },
}
</script>
